import { Box, Paper, Typography } from "@mui/material";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import SpaceDashboardRoundedIcon from "@mui/icons-material/SpaceDashboardRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import EngineeringRoundedIcon from "@mui/icons-material/EngineeringRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

import { Section } from "../section/section";

const STEPS = [
  { icon: LoginRoundedIcon, label: "Login" },
  { icon: SpaceDashboardRoundedIcon, label: "Dashboard" },
  { icon: Inventory2RoundedIcon, label: "Product Management" },
  { icon: EngineeringRoundedIcon, label: "Manufacturing Planning" },
  { icon: FactCheckRoundedIcon, label: "Production Tracking" },
  { icon: LocalShippingRoundedIcon, label: "Delivery Release" },
  { icon: PaidRoundedIcon, label: "Revenue Monitoring" },
  { icon: QueryStatsRoundedIcon, label: "Business Analytics" },
];

function Workflow() {
  return (
    <Section
      id="workflow"
      sx={{
        position: "relative",
        overflow: "hidden",
        py: { xs: 10, md: 16 },
        background:
          "linear-gradient(180deg, #f8fafc 0%, #eef6ff 50%, #f8fafc 100%)",
      }}
    >
      {/* Background Blobs */}
      <Box
        sx={{
          position: "absolute",
          top: -200,
          left: -200,
          width: 600,
          height: 600,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(59,130,246,0.22), transparent 60%)",
          filter: "blur(100px)",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          top: -180,
          right: -220,
          width: 600,
          height: 600,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(14,165,233,0.18), transparent 60%)",
          filter: "blur(110px)",
        }}
      />

      {/* Heading */}
      <Box sx={{ textAlign: "center", mb: 6 }}>
        <Typography
          sx={{
            fontSize: "0.8rem",
            fontWeight: 800,
            letterSpacing: 1.5,
            color: "#2563eb",
            textTransform: "uppercase",
            mb: 1,
          }}
        >
          Workflow
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: "2rem", md: "2.6rem" },
            fontWeight: 900,
            color: "#0f172a",
            letterSpacing: "-0.02em",
            mb: 1,
          }}
        >
          One connected flow, end to end
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: "1rem", md: "1.1rem" },
            color: "#475569",
            maxWidth: 700,
            mx: "auto",
          }}
        >
          Follow your business from login to analytics — every stage feeds the next
          for full operational visibility.
        </Typography>
      </Box>

      {/* GRID */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(4, 1fr)",
          },
          gap: { xs: 3, md: 5 },
          maxWidth: 1100,
          mx: "auto",
        }}
      >
        {STEPS.map((step, i) => {
          const Icon = step.icon;
          const isLast = i === STEPS.length - 1;

          // true row-based arrow logic
          const isEndOfRow = (i + 1) % 4 === 0;

          return (
            <Box key={step.label} sx={{ position: "relative" }}>
              {/* CARD */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 4,
                  height: 190,

                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  gap: 1.5,

                  background: "#ffffff",
                  border: "1px solid rgba(226,232,240,0.9)",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.06)",

                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow: "0 30px 80px rgba(0,0,0,0.12)",
                    borderColor: "rgba(37,99,235,0.35)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    background:
                      "linear-gradient(135deg, rgba(59,130,246,0.15), rgba(14,165,233,0.15))",
                  }}
                >
                  <Icon sx={{ color: "#2563eb", fontSize: 28 }} />
                </Box>

                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: 1.5,
                    color: "#64748b",
                  }}
                >
                  STEP {i + 1}
                </Typography>

                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 700,
                    color: "#0f172a",
                  }}
                >
                  {step.label}
                </Typography>
              </Paper>

              {/* ARROW FIXED POSITIONING */}
              {!isLast && !isEndOfRow && (
                <Box
                  sx={{
                    position: "absolute",

                    // ✅ PERFECT CENTER BETWEEN CARDS
                    right: -28,
                    top: "50%",
                    transform: "translateY(-50%)",

                    display: { xs: "none", md: "flex" },
                    alignItems: "center",
                    justifyContent: "center",

                    color: "#2563eb",
                    zIndex: 2,
                  }}
                >
                  <ArrowForwardRoundedIcon
                    sx={{
                      fontSize: 28,
                      animation: "moveArrow 1.4s ease-in-out infinite",
                      "@keyframes moveArrow": {
                        "0%,100%": { transform: "translateX(0)" },
                        "50%": { transform: "translateX(6px)" },
                      },
                    }}
                  />
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Section>
  );
}

export default Workflow;